import type { SSEMessage } from '@/_shared/types';

/**
 * Tools whose calls are plumbing, not work the user should see: `mcp__ui__*` renders the answer.
 * `mcp__assist__*` is a retired in-turn tool (assist now runs after the turn); chats saved while it
 * existed still carry its calls, and they stay hidden.
 */
function isHiddenTool(name: string): boolean {
  return name.startsWith('mcp__ui__') || name.startsWith('mcp__assist__');
}

/** Where a turn's answer ends: an assist card held back for the turn goes just above any of these. */
function endsTurnAnswer(msg: SSEMessage): boolean {
  if (msg.type === 'ui_message') return msg.uiType === 'recommend_actions';
  return (
    msg.type === 'turn_complete' || msg.type === 'turn_aborted' || msg.type === 'error' || msg.type === 'user_message'
  );
}

export interface ActivityItem {
  toolUse: {
    name: string;
    friendlyName: string;
    category: string;
    action: string;
    args: unknown;
    id: string;
    summary?: string;
    groupedTitle?: string;
    detail?: { type: 'code'; content: string; language: string } | null;
  };
  toolResult?: { id: string; content: string };
}

export type GroupedMessage =
  | { kind: 'single'; message: SSEMessage; index: number }
  | { kind: 'activity'; items: ActivityItem[]; startIndex: number; context?: string };

export function groupMessages(messages: SSEMessage[]): GroupedMessage[] {
  const result: GroupedMessage[] = [];

  // Track skipped hidden-tool tool_use IDs so we also skip their results
  const skippedIds = new Set<string>();
  let currentActivity: ActivityItem[] | null = null;
  let activityStartIndex = 0;

  // Capture the last assistant text to attach as context on the next activity group
  let pendingContext: string | null = null;
  let activityContext: string | null = null;

  // Singles emitted mid-group that must render BELOW the group that produced them
  const pendingAfterActivity: GroupedMessage[] = [];
  // assist_suggestion cards held until the turn's answer is done (see the ui_message branch below)
  const pendingAssist: GroupedMessage[] = [];
  // Where this turn's answer ended in `result`, once it has. Null until then, and again after a user_message.
  let answerEndPos: number | null = null;

  function flushAssist(): void {
    result.push(...pendingAssist.splice(0));
  }

  function flushActivity(): void {
    if (currentActivity && currentActivity.length > 0) {
      result.push({
        kind: 'activity',
        items: currentActivity,
        startIndex: activityStartIndex,
        ...(activityContext ? { context: activityContext } : {}),
      });
    }
    currentActivity = null;
    activityContext = null;
    result.push(...pendingAfterActivity.splice(0));
  }

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i]!;

    if (msg.type === 'tool_use') {
      if (isHiddenTool(msg.name)) {
        skippedIds.add(msg.id);
        continue;
      }

      if (!currentActivity) {
        currentActivity = [];
        activityStartIndex = i;
        activityContext = pendingContext;
        pendingContext = null;
      }

      currentActivity.push({
        toolUse: {
          name: msg.name,
          friendlyName: msg.friendlyName,
          category: msg.category,
          action: msg.action,
          args: msg.args,
          id: msg.id,
          summary: msg.summary,
          groupedTitle: msg.groupedTitle,
          detail: msg.detail,
        },
      });
      continue;
    }

    if (msg.type === 'tool_result') {
      if (skippedIds.has(msg.id)) {
        continue;
      }

      if (currentActivity) {
        const match = currentActivity.find((item) => item.toolUse.id === msg.id);
        if (match) {
          match.toolResult = { id: msg.id, content: msg.content };
          continue;
        }
      }

      // Orphan tool_result — skip silently
      continue;
    }

    // Assistant messages are transient — track the latest one as pending context
    // for the next activity group. They don't break groups.
    // Only update if non-empty (empty messages are streaming artifacts).
    if (msg.type === 'assistant') {
      if (msg.content) pendingContext = msg.content;
      continue;
    }

    // Result messages have no visual representation — skip without breaking groups.
    if (msg.type === 'result') {
      continue;
    }

    // dashboard_artifact ui_messages are sidecar records for the tool-activity cards; the server
    // persists one between a preview/update tool_use and its tool_result, so they must NOT break the
    // group (that would orphan the tool_result and drop its card). Rendering flows from the tool row.
    if (msg.type === 'ui_message' && msg.uiType === 'dashboard_artifact') {
      continue;
    }

    // audience_artifact ui_messages are emitted by save_audience_draft SYNCHRONOUSLY between its
    // tool_use and its own tool_result (src/mcp/audience.ts), but unlike dashboard_artifact this card
    // renders on ITS OWN (not from the tool row) — so it must NOT be `continue`-skipped, that would
    // delete the card. Do NOT flush the open activity group either, so the group stays open and the
    // trailing tool_result still finds its tool_use and pairs instead of orphaning. Because the group
    // is still buffered, defer the card until the group is pushed — otherwise it renders above the
    // activity that produced it.
    if (msg.type === 'ui_message' && msg.uiType === 'audience_artifact') {
      const card: GroupedMessage = { kind: 'single', message: msg, index: i };
      if (currentActivity) pendingAfterActivity.push(card);
      else result.push(card);
      continue;
    }

    // assist_suggestion cards are written after the turn ends (src/agent/assist-run.ts), so the card's
    // row sits after the turn's chips and turn_complete. Chats saved under the retired in-turn tool can
    // carry one anywhere in the turn. Either way, place it where the answer ends, just above the
    // follow-up chips, so it never splits an activity group or sits above the answer it is about.
    if (msg.type === 'ui_message' && msg.uiType === 'assist_suggestion') {
      const card: GroupedMessage = { kind: 'single', message: msg, index: i };
      if (answerEndPos === null) pendingAssist.push(card);
      else result.splice(answerEndPos++, 0, card);
      continue;
    }

    // Visible non-tool message breaks the activity group
    flushActivity();
    if (endsTurnAnswer(msg)) {
      flushAssist();
      if (msg.type === 'user_message') answerEndPos = null;
      else answerEndPos ??= result.length;
    }
    pendingContext = null;
    result.push({ kind: 'single', message: msg, index: i });
  }

  flushActivity();
  flushAssist();

  return result;
}

export function isToolActive(messages: SSEMessage[]): boolean {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i]!;
    if (msg.type === 'tool_use' && isHiddenTool(msg.name)) continue;
    if (msg.type === 'tool_result') continue;
    return msg.type === 'tool_use';
  }
  return false;
}
